import React from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { Socket } from 'phoenix-socket'
import { flatten } from 'lodash'

import { reportAutoSuggest } from '../../../common/analytics/searchBarAnalytics'
import autoSuggestEndpoint from '../../../common/constants/autoSuggestEndpoints'
import { makeLink, parseLocation } from '../../../common/utils/urlHelper'
import { WebSocketError, logError } from '../../../common/utils/errors'
import SearchBarForm from '../searchBar/searchBarForm'
import { getTopCategoryLabel } from '../../../config/sites/categories'
import RecentSearch from './recentSearch'

export default AutoSuggest => {
  class AutoSuggestDataContainer extends React.Component {
    static propTypes = {
      inputName: PropTypes.string.isRequired,
      placeholder: PropTypes.string,
      location: PropTypes.object.isRequired
    }

    static defaultProps = {
      placeholder: ''
    }

    constructor(props) {
      super(props)
      this.state = {
        suggestions: [],
        inputValue: props.value || ''
      }

      this.socketConnection = null
      this.socketChannel = null

      this.setSuggestions = this.setSuggestions.bind(this)
      this.onInputChange = this.onInputChange.bind(this)
      this.onSelectOption = this.onSelectOption.bind(this)
      this.onInputFocus = this.onInputFocus.bind(this)
    }

    componentDidMount() {
      this.initSocket()
    }

    componentWillUnmount() {
      this.closeSocket()
    }

    componentWillReceiveProps(nextProps) {
      if (nextProps.value !== this.props.value) {
        this.setState({
          inputValue: nextProps.value
        })
      }
    }

    initSocket() {
      const { endpoint, channel } = autoSuggestEndpoint

      this.socketConnection = new Socket(endpoint)
      this.socketChannel = this.socketConnection.channel(channel, {})

      this.socketConnection.connect()

      this.socketChannel.on('new_msg', message => {
        this.setSuggestions(message.body)
      })

      this.socketChannel
        .join()
        .receive('ok', () => {
          if (this.state.inputValue) {
            this.sendMessage(this.state.inputValue)
          }
        })
        .receive('error', reason => {
          logError(WebSocketError({ message: reason }))
        })
    }

    closeSocket() {
      if (this.socketConnection) {
        this.socketConnection.disconnect()
      }
    }

    sendMessage(value = '') {
      const { currentSite } = this.props

      const message = {
        body: value.trim().toLowerCase(),
        category: null,
        site: currentSite || 'themeforest'
      }

      if (
        this.socketConnection &&
        this.socketChannel &&
        this.socketConnection.isConnected() &&
        this.socketChannel.isJoined()
      ) {
        this.socketChannel.push('new_msg', message).receive('error', reason => {
          logError(WebSocketError({ message: reason }))
        })
      }
    }

    setSuggestions(suggestions) {
      this.setState({
        suggestions
      })
    }

    onInputChange(value = '') {
      this.setState({
        inputValue: value
      })
      if (value === '') {
        this.setSuggestions(RecentSearch.getRecentSearch())
      } else {
        this.sendMessage(value)
      }
    }

    getLink(term) {
      const { location } = this.props

      let nextQueries = Object.assign({}, parseLocation(location), {
        term
      })
      // reset page number
      delete nextQueries.page
      return makeLink(nextQueries)
    }

    onSelectOption(option, event) {
      const { history } = this.props
      const { suggestions } = this.state

      let nextOption

      // if the selection option is a string, we need to build the link
      if (typeof option === 'string') {
        nextOption = {
          text: option,
          link: this.getLink(option)
        }
        // if the selection option is an object, it means it's triggered by auto-suggest
        // we need to report this to ga
      } else if (typeof option === 'object') {
        nextOption = Object.assign({}, option)
        reportAutoSuggest({
          action: event && event.type === 'mousedown' ? 'click' : 'keyboard',
          label: option.text,
          suggestedTerm: suggestions.map(suggestion => {
            return suggestion.text
          })
        })

        if (nextOption.type === 'p') {
          nextOption.link = this.getLink(option.text)
        }
      }

      if (nextOption && nextOption.text) {
        RecentSearch.addItem(nextOption.text)
        this.setState(
          {
            inputValue: nextOption.text
          },
          () => {
            history.push(nextOption.link)
          }
        )
      }
    }

    onInputFocus(value = '') {
      if (value === '') {
        this.setState({ suggestions: RecentSearch.getRecentSearch() })
      }
    }

    getCategorySuggestions() {
      const { suggestions } = this.state
      const { currentSite } = this.props

      const categorySuggestions = suggestions
        .filter(suggestion => suggestion.type === 'c')
        .map(suggestion => {
          return suggestion.categories.map(categoryKey => ({
            link: suggestion.link + categoryKey,
            type: suggestion.type,
            text: suggestion.text,
            category:
              getTopCategoryLabel(currentSite, categoryKey) || categoryKey
          }))
        })

      return flatten(categorySuggestions)
    }

    getSuggestions() {
      const { showCategories } = this.props
      const { suggestions } = this.state

      const nonCategorySuggestions = suggestions.filter(
        suggestion => suggestion.type !== 'c'
      )

      if (showCategories) {
        return this.getCategorySuggestions().concat(nonCategorySuggestions)
      }

      return nonCategorySuggestions
    }

    render() {
      const { inputValue } = this.state
      const { location } = this.props

      const autosuggestProps = {
        ...this.props,
        suggestions: this.getSuggestions(),
        onInputChange: this.onInputChange,
        onSelectOption: this.onSelectOption,
        inputValue,
        type: 'search',
        onInputFocus: this.onInputFocus
      }
      return (
        <SearchBarForm location={location}>
          <AutoSuggest {...autosuggestProps} />
        </SearchBarForm>
      )
    }
  }

  return withRouter(AutoSuggestDataContainer)
}



// WEBPACK FOOTER //
// ./client/components/autosuggest/autoSuggestDataContainer.js